/**
 * Server state.
 *
 * Every screen reads data through these hooks. Two consequences worth naming:
 * the impact statistics are fetched once and shared by all sixteen footers,
 * and no component can accidentally hold a stale total, because the quote is
 * refetched whenever the meal or quantity changes.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';

import type {
  FulfilmentType,
  AppConfig,
  CreateDonationRequest,
  Donation,
  DonationFilter,
  DonationSort,
  DonationSummary,
  DonationTracking,
  DistributionProof,
  DonorImpact,
  DonorProfile,
  HowItWorksStep,
  ImpactStats,
  InitiatePaymentRequest,
  Meal,
  NotificationItem,
  NotificationPreferences,
  Paginated,
  PaymentIntent,
  PaymentRecord,
  PaymentMethodOption,
  Quote,
  QuoteRequest,
  CreateTiffinOrderRequest,
  TiffinAvailability,
  TiffinMenu,
  TiffinOrder,
  TiffinOrderSummary,
  TiffinQuote,
  TiffinQuoteRequest,
} from '@mfa/domain';

import { config } from '../../app/config';
import { useIsSignedIn } from '../auth/useAuth';
import { api } from './client';
import { rememberConfig } from './configCache';
import { installationHeaders } from '../identity/installation';

/** Query keys, centralised so invalidations cannot miss a cache entry. */
export const keys = {
  stats: ['stats'] as const,
  howItWorks: ['how-it-works'] as const,
  meals: ['meals'] as const,
  config: ['config'] as const,
  paymentMethods: ['payment-methods'] as const,
  quote: (
    mealId: string | null,
    people: number | null,
    fulfilment: string,
    city: string | null,
  ) => ['quote', mealId, people, fulfilment, city] as const,
  donation: (id: string) => ['donation', id] as const,
  tracking: (id: string) => ['donation', id, 'tracking'] as const,
  proof: (id: string) => ['donation', id, 'proof'] as const,
  myDonations: (filter: DonationFilter, sort: DonationSort) =>
    ['me', 'donations', filter, sort] as const,
  myImpact: ['me', 'impact'] as const,
  myProfile: ['me', 'profile'] as const,
  notifications: ['me', 'notifications'] as const,
  unreadCount: ['me', 'notifications', 'unread'] as const,
  tiffinMenu: ['tiffin', 'menu'] as const,
  tiffinAvailability: (date: string | null) => ['tiffin', 'availability', date] as const,
  tiffinQuote: (request: TiffinQuoteRequest | null) =>
    ['tiffin', 'quote', request ? JSON.stringify(request) : null] as const,
  tiffinOrder: (id: string) => ['tiffin', 'order', id] as const,
  myTiffinOrders: ['me', 'tiffin-orders'] as const,
};

const staleTime = config.staleTimeSeconds * 1000;

type Options<T> = Omit<UseQueryOptions<T, Error, T>, 'queryKey' | 'queryFn'>;

// ----------------------------------------------------------------- reference

export const useImpactStats = (options?: Options<ImpactStats>) =>
  useQuery({
    queryKey: keys.stats,
    queryFn: () => api.get<ImpactStats>('home/stats', { auth: false }),
    staleTime,
    ...options,
  });

export const useHowItWorks = () =>
  useQuery({
    queryKey: keys.howItWorks,
    queryFn: () => api.get<HowItWorksStep[]>('how-it-works', { auth: false }),
    // Editorial copy: safe to cache for the session.
    staleTime: Infinity,
  });

export const useMeals = () =>
  useQuery({
    queryKey: keys.meals,
    queryFn: () => api.get<Meal[]>('meals', { auth: false }),
    staleTime,
  });

/** The server's settings, remembered on the device for the next start. */
export async function fetchConfig(): Promise<AppConfig> {
  const config = await api.get<AppConfig>('config', { auth: false });
  void rememberConfig(config);
  return config;
}

export const useAppConfig = () =>
  useQuery({
    queryKey: keys.config,
    queryFn: fetchConfig,
    staleTime,
  });

export const usePaymentMethods = () =>
  useQuery({
    queryKey: keys.paymentMethods,
    queryFn: () => api.get<PaymentMethodOption[]>('payment-methods', { auth: false }),
    staleTime,
  });

// --------------------------------------------------------------------- quote

/**
 * The authoritative price for a meal and headcount.
 *
 * Disabled until both are chosen. Quotes are never cached beyond their
 * lifetime — `staleTime: 0` means every visit to Review re-reads the price the
 * server will actually charge.
 */
export const useQuote = (
  mealId: string | null,
  peopleCount: number | null,
  /**
   * Delivery is priced into the quote, so a change of fulfilment has to
   * re-quote — it is part of the key, not an afterthought applied to the
   * total. A quote issued for charity distribution cannot buy a delivery.
   */
  fulfilment: { type: FulfilmentType; city?: string | null } = {
    type: 'CHARITY_DISTRIBUTION',
  },
) =>
  useQuery({
    queryKey: keys.quote(mealId, peopleCount, fulfilment.type, fulfilment.city ?? null),
    queryFn: () => {
      // `enabled` below guarantees both are set by the time this runs.
      const request: QuoteRequest = {
        mealId: mealId!,
        peopleCount: peopleCount!,
        fulfilmentType: fulfilment.type,
        ...(fulfilment.city ? { deliveryCity: fulfilment.city } : {}),
      };
      return api.post<Quote>('donations/quote', request, { auth: false });
    },
    enabled: Boolean(mealId) && Boolean(peopleCount),
    staleTime: 0,
    gcTime: 0,
  });

// ----------------------------------------------------------------- donations

export const useCreateDonation = (
  options?: UseMutationOptions<Donation, Error, CreateDonationRequest>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateDonationRequest) =>
      // The idempotency key also travels as a header so a proxy retry is safe.
      // The installation header is what ties this donation to this device, so
      // it appears in My Donations afterwards without an account.
      api.post<Donation>('donations', payload, {
        idempotencyKey: payload.idempotencyKey,
        headers: installationHeaders,
      }),
    onSuccess: (donation) => {
      queryClient.setQueryData(keys.donation(donation.id), donation);
      void queryClient.invalidateQueries({ queryKey: ['me', 'donations'] });
      void queryClient.invalidateQueries({ queryKey: keys.stats });
    },
    ...options,
  });
};

/**
 * One donation, read as whoever is asking.
 *
 * The installation credential travels with it: a guest owns the donations
 * made from their device, and without it the screen shown straight after
 * paying could not read the donation it had just created.
 */
export const useDonation = (id: string | null) =>
  useQuery({
    queryKey: keys.donation(id ?? ''),
    queryFn: () => api.get<Donation>(`donations/${id}`, { headers: installationHeaders }),
    enabled: Boolean(id),
  });

export const useTracking = (id: string | null) =>
  useQuery({
    queryKey: keys.tracking(id ?? ''),
    queryFn: () =>
      api.get<DonationTracking>(`donations/${id}/tracking`, { headers: installationHeaders }),
    enabled: Boolean(id),
    // Tracking moves while the donor watches; refresh on focus.
    staleTime: 15_000,
  });

export const useProof = (id: string | null, enabled = true) =>
  useQuery({
    queryKey: keys.proof(id ?? ''),
    queryFn: () =>
      api.get<DistributionProof>(`donations/${id}/proof`, { headers: installationHeaders }),
    enabled: Boolean(id) && enabled,
    retry: false,
  });

export const useLookupDonation = () =>
  useMutation({
    mutationFn: (input: { publicReference: string; mobileNumber: string }) =>
      api.post<Donation>('donations/lookup', input, { auth: false }),
  });

// ------------------------------------------------------------------ payments

export const useInitiatePayment = (
  options?: UseMutationOptions<PaymentIntent, Error, InitiatePaymentRequest>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: InitiatePaymentRequest) =>
      api.post<PaymentIntent>('payments/initiate', payload),
    onSuccess: (_intent, variables) => {
      // The id is a donation's or a tiffin order's; invalidating the kind it
      // is not costs nothing.
      void queryClient.invalidateQueries({ queryKey: keys.donation(variables.donationId) });
      void queryClient.invalidateQueries({ queryKey: keys.tracking(variables.donationId) });
      void queryClient.invalidateQueries({ queryKey: keys.tiffinOrder(variables.donationId) });
      void queryClient.invalidateQueries({ queryKey: keys.myTiffinOrders });
    },
    ...options,
  });
};

/**
 * Ask the server whether a payment the donor is approving elsewhere has
 * settled. The answer is the provider's, verified server-side; the app only
 * ever reports it.
 */
export const useRefreshPayment = () =>
  useMutation({
    mutationFn: (paymentId: string) =>
      api.post<PaymentRecord>(`payments/${paymentId}/refresh`, {}),
  });

export const usePayment = (id: string | null) =>
  useQuery({
    queryKey: ['payment', id ?? ''],
    queryFn: () => api.get<PaymentRecord>(`payments/${id}`),
    enabled: Boolean(id),
  });

/** The donor's phone, in the demo. The server refuses it for real payments. */
export const useDemoPaymentDecision = () =>
  useMutation({
    mutationFn: (input: { paymentId: string; approve: boolean }) =>
      api.post<PaymentRecord>(`payments/${input.paymentId}/demo-decision`, {
        approve: input.approve,
      }),
  });

// ------------------------------------------------------------------------ me

/**
 * The `me/*` endpoints require a session.
 *
 * Each is gated on one, so a signed-out visitor does not trigger a run of 401s
 * — and so the screens can distinguish "you have no donations" from "you are
 * not signed in", which are very different things to show someone.
 */
/**
 * Donation history for whoever is asking — an account or this installation.
 *
 * Not gated on being signed in any more: the whole point of the installation
 * credential is that a guest has a history too. The server decides what the
 * caller may see and returns nothing rather than everything when it cannot
 * identify them.
 */
/**
 * My Orders lists both kinds in one screen, merged here, so each list comes
 * whole — the server's largest page — rather than twenty of one kind
 * interleaved with twenty of the other.
 */
const MY_ORDERS_PAGE = 100;

export const useMyDonations = (filter: DonationFilter = 'all', sort: DonationSort = 'newest') =>
  useQuery({
    queryKey: keys.myDonations(filter, sort),
    queryFn: () =>
      api.get<Paginated<DonationSummary>>('me/donations', {
        query: { filter, sort, pageSize: MY_ORDERS_PAGE },
        headers: installationHeaders,
      }),
  });

/**
 * This donor's own totals, owned exactly as the history above is.
 *
 * Gating it on being signed in left a guest — the common case — looking at a
 * skeleton that never resolved, because a disabled query never loads and never
 * errors. It sends the installation credential and lets the server answer.
 */
export const useMyImpact = () =>
  useQuery({
    queryKey: keys.myImpact,
    queryFn: () => api.get<DonorImpact>('me/impact', { headers: installationHeaders }),
  });

export const useMyProfile = () => {
  const signedIn = useIsSignedIn();
  return useQuery({
    queryKey: keys.myProfile,
    queryFn: () => api.get<DonorProfile>('me'),
    enabled: signedIn,
  });
};

export const useNotifications = () => {
  const signedIn = useIsSignedIn();
  return useQuery({
    queryKey: keys.notifications,
    queryFn: () => api.get<Paginated<NotificationItem>>('me/notifications'),
    enabled: signedIn,
  });
};

export const useUnreadCount = () => {
  const signedIn = useIsSignedIn();
  return useQuery({
    queryKey: keys.unreadCount,
    queryFn: () => api.get<{ count: number }>('me/notifications/unread-count'),
    enabled: signedIn,
    staleTime: 30_000,
  });
};

export const useMarkNotificationsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationId?: string) =>
      api.post<void>('me/notifications/read', undefined, {
        query: notificationId ? { notificationId } : undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.notifications });
      void queryClient.invalidateQueries({ queryKey: keys.unreadCount });
    },
  });
};

export const useUpdatePreferences = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<Record<string, boolean>>) =>
      api.patch<NotificationPreferences>('me/notification-preferences', patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.myProfile });
    },
  });
};


// -------------------------------------------------------------------- tiffin

/** The whole active menu; each screen filters it to the chosen meal. */
export const useTiffinMenu = () =>
  useQuery({
    queryKey: keys.tiffinMenu,
    queryFn: () => api.get<TiffinMenu>('tiffin/menu', { auth: false }),
    staleTime,
  });

/**
 * Which meals on a date are still taking orders, on the kitchen's clock.
 *
 * Refreshed every minute while the screen is open: a customer choosing lunch
 * at 10:29 should see it close at 10:30, not be told so after paying.
 */
export const useTiffinAvailability = (date: string | null) =>
  useQuery({
    queryKey: keys.tiffinAvailability(date),
    queryFn: () =>
      api.get<TiffinAvailability>('tiffin/availability', {
        auth: false,
        query: { date: date ?? undefined },
      }),
    enabled: Boolean(date),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

/**
 * The server's price for the current selection. Null disables it: nothing is
 * quoted until there is a meal, at least one item and a way to receive it.
 */
export const useTiffinQuote = (request: TiffinQuoteRequest | null) =>
  useQuery({
    queryKey: keys.tiffinQuote(request),
    queryFn: () => api.post<TiffinQuote>('tiffin/quote', request, { auth: false }),
    enabled: Boolean(request),
    staleTime: 0,
    gcTime: 0,
    // A closed meal or an item gone from the menu will not reopen on retry.
    retry: false,
  });

export const useCreateTiffinOrder = (
  options?: UseMutationOptions<TiffinOrder, Error, CreateTiffinOrderRequest>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTiffinOrderRequest) =>
      api.post<TiffinOrder>('tiffin/orders', payload, {
        idempotencyKey: payload.idempotencyKey,
        headers: installationHeaders,
      }),
    onSuccess: (order) => {
      queryClient.setQueryData(keys.tiffinOrder(order.id), order);
      void queryClient.invalidateQueries({ queryKey: keys.myTiffinOrders });
    },
    ...options,
  });
};

/** One tiffin order with its tracking, as the device that placed it. */
export const useTiffinOrder = (id: string | null) =>
  useQuery({
    queryKey: keys.tiffinOrder(id ?? ''),
    queryFn: () => api.get<TiffinOrder>(`tiffin/orders/${id}`, { headers: installationHeaders }),
    enabled: Boolean(id),
    // An order moves while the customer watches the kitchen work on it, and
    // the copy cached when it was placed predates its payment: every screen
    // that opens it asks again.
    staleTime: 15_000,
    refetchOnMount: 'always',
  });

export const useMyTiffinOrders = (
  filter: DonationFilter = 'all',
  sort: DonationSort = 'newest',
) =>
  useQuery({
    queryKey: [...keys.myTiffinOrders, filter, sort] as const,
    queryFn: () =>
      api.get<Paginated<TiffinOrderSummary>>('me/tiffin-orders', {
        query: { filter, sort, pageSize: MY_ORDERS_PAGE },
        headers: installationHeaders,
      }),
  });
